import { Link } from "react-router-dom";
import { useT } from "../../lib/i18n";
import { ArrowRight } from "lucide-react";
import { TeeIcon, CapIcon, NeedleIcon } from "../../components/site/EntryIcons";
import { OFFERS, CONTACT } from "../../content/site";
import { Placeholder } from "../../components/site/Placeholder";
import { Reviews } from "../../components/site/Reviews";
import { GoogleBadge } from "../../components/site/GoogleBadge";
import { PackSection } from "../../components/site/PackSection";
import { useSeo, ORG_LD } from "../../lib/seo";

const ICONS = { vetements: TeeIcon, accessoires: CapIcon, evenement: NeedleIcon };

const PLAIN = { vetements: "Vêtements", accessoires: "Casquettes & accessoires", evenement: "Événement" };
const ONE_LINE = { vetements: "T-shirts, polos, sweats, vestes, tenues de travail — brodés ou imprimés à votre logo.", accessoires: "Casquettes, bonnets, bobs, tote bags, serviettes — en série, à votre marque.", evenement: "Une brodeuse et son équipe chez vous : prénoms et initiales brodés en direct." };

export function EntryTile({ k, i }) {
  const o = OFFERS[k];
  const { t } = useT();
  const Icon = ICONS[k];
  const img = k === "vetements" ? o.image : (o.home || o.image);
  const alt = k === "vetements" ? (o.home || o.image.more) : (o.image.more || o.image);
  return (
    <Link to={`/demande/${k}`} data-testid={`card-${k}`} style={{ animationDelay: `${i * 120}ms` }}
      className="k-reveal group k-card flex flex-col overflow-hidden hover:border-[#C25E38] hover:shadow-md">
      <div className="relative">
        <div className="relative aspect-[4/3] overflow-hidden bg-[#EDE7DD]" data-testid={`card-${k}-photos`}>
          <img src={img.src} alt={img.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0" style={{ objectPosition: img.position }} />
          <img src={alt.src} alt={alt.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-0 scale-105 transition-[opacity,transform] duration-700 group-hover:opacity-100 group-hover:scale-100" style={{ objectPosition: alt.position || "center" }} aria-hidden />
        </div>
        <span className="absolute left-5 bottom-0 translate-y-1/2 w-20 h-20 rounded-full bg-[#FBF9F5] border border-[#1C1917] flex items-center justify-center shadow-md transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105" aria-hidden>
          <Icon className="w-12 h-12 text-[#1C1917]" />
        </span>
      </div>
      <div className="p-6 sm:p-8 pt-14 sm:pt-14 flex flex-col flex-1">
        <span className="k-eyebrow">{o.num}</span>
        <h3 className="font-display text-3xl sm:text-4xl mt-2 leading-tight" data-testid={`card-${k}-title`}>{t(PLAIN[k])}</h3>
        <p className="text-sm text-[#57534E] mt-3 leading-relaxed flex-1">{t(ONE_LINE[k])}</p>
        <span className="k-btn mt-6 self-start group-hover:bg-[#C25E38] group-hover:border-[#C25E38]">{t(o.cta)} <ArrowRight size={16} /></span>
      </div>
    </Link>
  );
}

export function EntryItem({ k, i }) {
  const o = OFFERS[k];
  const { t } = useT();
  const Icon = ICONS[k];
  return (
    <Link to={`/demande/${k}`} data-testid={`card-${k}`} style={{ animationDelay: `${i * 120}ms` }}
      className="k-reveal group k-card flex flex-col p-6 sm:p-7 hover:border-[#C25E38] hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <span className="k-eyebrow">{o.num} — {t(o.title)}</span>
        <Icon className="w-16 h-16 shrink-0 text-[#1C1917] transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110" />
      </div>
      <h3 className="font-display text-2xl mt-4 leading-tight">{t(o.tagline)}</h3>
      <p className="text-sm text-[#57534E] mt-3 leading-relaxed flex-1">{t(o.short)}</p>
      <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mt-6 group-hover:text-[#C25E38] transition-colors">{t(o.cta)} <ArrowRight size={14} /></span>
    </Link>
  );
}

export function EntryCard({ k, i, featured = false }) {
  const o = OFFERS[k];
  const { t } = useT();
  if (featured) return (
    <Link to={`/demande/${k}`} data-testid={`card-${k}`} style={{ animationDelay: `${i * 120}ms` }}
      className="k-reveal group k-card k-weave-dark text-[#FBF9F5] h-full flex flex-col border-[#1C1917] hover:border-[#C25E38] hover:shadow-md p-8 sm:p-10">
      <div className="flex items-start justify-between gap-4">
        <span className="k-eyebrow">{o.num} — {t(o.title)} · {t("Notre spécialité")}</span>
        <NeedleIcon className="w-20 h-20 shrink-0 transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110" />
      </div>
      <h3 className="font-display text-3xl sm:text-4xl mt-6 leading-tight">{t(o.tagline)}</h3>
      <p className="text-base text-[#D6CEBF] mt-4 leading-relaxed flex-1 max-w-md">{t(o.short)}</p>
      <ul className="mt-6 flex flex-wrap gap-2 text-xs text-[#D6CEBF]">
        {["Installation en moins d'une heure", "Prénoms et initiales en direct", "Hôtels, boutiques, séminaires, mariages"].map((x) => <li key={x} className="border border-white/20 px-3 py-1">{t(x)}</li>)}
      </ul>
      <span className="k-btn mt-8 self-start bg-[#C25E38] border-[#C25E38] group-hover:bg-[#FBF9F5] group-hover:text-[#1C1917] group-hover:border-[#FBF9F5]">{t(o.cta)} <ArrowRight size={16} /></span>
    </Link>
  );
  return (
    <Link to={`/demande/${k}`} data-testid={`card-${k}`} style={{ animationDelay: `${i * 120}ms` }}
      className="k-reveal group k-card flex flex-col hover:border-[#C25E38] hover:shadow-md">
      <Placeholder className="aspect-[4/3] md:aspect-[4/5]" caption={t(o.title)} label={`${o.num} · Photo à venir`} src={(o.home || o.image)?.src} alt={(o.home || o.image)?.alt} position={(o.home || o.image)?.position} />
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <span className="k-eyebrow">{o.num} — {t(o.title)}</span>
        <h3 className="k-h3 mt-3 leading-tight">{t(o.tagline)}</h3>
        <p className="text-sm text-[#57534E] mt-3 leading-relaxed flex-1">{t(o.short)}</p>
        <span className="k-btn mt-6 self-start group-hover:bg-[#C25E38] group-hover:border-[#C25E38]">{t(o.cta)} <ArrowRight size={16} /></span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { t } = useT();
  useSeo({ title: "Vêtements personnalisés pour équipes, entreprises et événements — Atelier KOUOH Nice", path: "/",
    description: "Choisissez vos vêtements, partagez votre besoin : l'atelier KOUOH à Nice vous conseille et prépare votre devis. T-shirts, polos, sweats, vestes, casquettes brodés ou imprimés à votre logo.", jsonLd: ORG_LD });
  return (
    <>
      <section className="relative min-h-[70vh] flex items-end bg-[#1C1917] text-[#FBF9F5] overflow-hidden" data-testid="hero">
        <img src="/photos/ecussons-nissa-explore.jpg" alt="Écussons brodés multicolores Nissa Explore réalisés à l'atelier KOUOH" className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid="hero-photo" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-transparent" aria-hidden />
        <div className="relative k-section pt-28 md:pt-40 pb-14 w-full k-reveal">
          <p className="k-eyebrow text-[#D6CEBF]">{t("Atelier KOUOH · Nice")}</p>
          <h1 className="k-h1 mt-5 max-w-3xl" data-testid="home-title">{t("Vos vêtements à votre logo. Simplement.")}</h1>
          <p className="k-lead mt-5 max-w-xl text-[#D6CEBF]">{t("Choisissez, envoyez votre logo, recevez votre devis. L'atelier s'occupe du reste.")}</p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <Link to="/demande/vetements" className="k-btn bg-[#C25E38] border-[#C25E38]" data-testid="hero-cta-start">{t("Demander mon devis")} <ArrowRight size={16} /></Link>
            <span className="text-sm text-[#D6CEBF]" data-testid="hero-trust">{t("Gratuit · Sans engagement · Réponse rapide")}</span>
          </div>
          <GoogleBadge dark className="mt-5 text-[#FBF9F5]" />
        </div>
      </section>

      <section id="choisir" className="k-section scroll-mt-24" aria-labelledby="choisir-title" data-testid="home-entries">
        <h2 id="choisir-title" className="k-h2 max-w-2xl">{t("Par quoi commençons-nous ?")}</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {["vetements", "accessoires", "evenement"].map((k, i) => <EntryTile key={k} k={k} i={i} />)}
        </div>
        <p className="mt-6 text-sm flex flex-wrap gap-x-5"><Link to="/produits" className="k-link" data-testid="hero-cta-products">{t("Voir le vestiaire")}</Link><Link to="/projet-particulier" className="k-link" data-testid="hero-cta-project">{t("J'ai un projet particulier")}</Link></p>
      </section>

      <PackSection />

      <section className="k-section pt-0" data-testid="home-event">
        <Link to="/demande/evenement" className="group grid md:grid-cols-12 border border-[#1C1917] overflow-hidden hover:bg-[#1C1917] hover:text-[#FBF9F5] transition-colors" data-testid="home-event-link">
          <img src="/photos/evenement-anantara-brodeuse.jpg" alt="Brodeuse installée sur un événement, personnalisation en direct" loading="lazy" className="md:col-span-5 aspect-[4/3] md:aspect-auto w-full h-full object-cover" />
          <div className="md:col-span-7 p-8 sm:p-10 flex flex-col justify-center">
            <p className="k-eyebrow">{t("Notre second pôle")}</p>
            <h2 className="k-h2 mt-3">{t("Animation broderie sur votre événement.")}</h2>
            <p className="text-sm mt-4 opacity-80 max-w-xl leading-relaxed">{t("Une brodeuse et son équipe installées chez vous : prénoms, initiales et logos brodés en direct devant vos invités. Hôtels, boutiques, séminaires, mariages.")}</p>
            <span className="k-btn mt-8 self-start group-hover:bg-[#FBF9F5] group-hover:text-[#1C1917] group-hover:border-[#FBF9F5]">{t("Organiser mon événement")} <ArrowRight size={16} /></span>
          </div>
        </Link>
      </section>

      <section className="k-section" aria-labelledby="real-title" data-testid="home-realisations">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div><p className="k-eyebrow">{t("Réalisations")}</p><h2 id="real-title" className="k-h2 mt-3">{t("Des vêtements que des équipes portent déjà.")}</h2></div>
          <Link to="/realisations" className="k-link text-sm" data-testid="home-realisations-link">{t("Voir toutes les réalisations")}</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[["/photos/sweats-fc-beausoleil.jpg", "FC Beausoleil", "Sweats du club", "Sweats brodés pour les joueurs et le staff, logo cœur et nom au dos."], ["/photos/casquettes-champions-or.jpg", "Club sportif", "Casquettes des champions", "Série de casquettes brodées fil or pour célébrer un titre."], ["/photos/polo-five-stars-monte-carlo.jpg", "Five Stars Monte-Carlo", "Polos d'équipe", "Polos brodés pour une équipe de services premium à Monaco."]].map(([src, who, what, how]) => (
            <figure key={src} className="group">
              <div className="aspect-[4/3] overflow-hidden bg-[#EDE7DD]"><img src={src} alt={`${what} — ${who}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
              <figcaption className="mt-4"><p className="k-eyebrow">{who}</p><p className="font-display text-2xl mt-1">{t(what)}</p><p className="text-sm text-[#57534E] mt-1">{t(how)}</p></figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-[#1C1917] text-[#FBF9F5]" data-testid="home-steps">
        <div className="k-section">
          <p className="k-eyebrow">{t("Comment ça se passe")}</p>
          <h2 className="k-h2 mt-3 max-w-2xl">{t("Trois étapes, et nous nous occupons de tout.")}</h2>
          <ol className="mt-12 grid gap-8 md:grid-cols-3">
            {[["Vous choisissez", "Vous choisissez vos vêtements et nous expliquez votre besoin."], ["Nous conseillons", "Nous vous rappelons si besoin, vous conseillons sur les modèles et vous envoyons votre devis."], ["Nous réalisons", "Après validation du devis et du visuel à valider avant production, l'atelier lance la réalisation selon les conditions convenues."]].map(([st, d], i) => (
              <li key={st} className="border-t border-white/20 pt-5">
                <span className="font-mono-k text-xs text-[#C25E38]">0{i + 1}</span>
                <p className="font-display text-2xl mt-2">{t(st)}</p>
                <p className="text-sm text-[#D6CEBF] mt-2 leading-relaxed">{t(d)}</p>
              </li>
            ))}
          </ol>
          <ul className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#D6CEBF]" data-testid="home-commitments">
            {["Atelier à Nice", "Conseil dans le choix des vêtements", "Visuel à valider avant production", "Contrôle qualité pièce par pièce", "Prototype photographié selon l'offre"].map((x) => <li key={x} className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 bg-[#C25E38]" />{t(x)}</li>)}
          </ul>
        </div>
      </section>

      <Reviews />

      <section className="k-section grid gap-10 lg:grid-cols-12 items-center" data-testid="home-contact">
        <div className="lg:col-span-7">
          <p className="k-eyebrow">{t("Une question ?")}</p>
          <h2 className="k-h2 mt-3">{t("Un atelier, des personnes qui répondent.")}</h2>
          <p className="k-lead mt-5">{t("Écrivez-nous ou laissez votre numéro dans votre demande : nous vous rappelons.")}</p>
          <p className="mt-6 text-sm"><a href={`mailto:${CONTACT.email}`} className="k-link" data-testid="contact-email">{CONTACT.email}</a>{CONTACT.phone && <> · <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="k-link">{CONTACT.phone}</a></>} · {CONTACT.address}</p>
        </div>
        <div className="lg:col-span-5 flex lg:justify-end">
          <Link to="/demande/vetements" className="k-btn" data-testid="home-cta-bottom">{t("Demander mon devis")} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </>
  );
}
